import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import Style from './index.less';
import Menu from '@tencent/dui/lib/components/Menu';
import Divider from '@tencent/dui/lib/components/Divider';
import Tooltip from '@tencent/dui/lib/components/Tooltip';
import classNames from 'classnames';
import { getPlatformShortcut } from '../../../../js/utils';
import IntelligentToolDescriptionCard from '@com-pc/intelligent-tool-description-card';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import i18n from '@i18next/i18n';
import { getVipLinkText } from '../vip-link-text';

interface Config {
    id?: string;
    fix?: boolean;
    scrollable?: boolean;
    height?: number | string;
    top?: number;
    left?: number;
    subMinWidth?: number;
    right?: number;
    minWidth?: number;
    hasAnim?: boolean;
    zIndex?: number;
    bodyClickCallBack?: (e: MouseEvent, clickOnMenu: boolean) => boolean;
    onMenuHideCallBack?: () => void;
    onVisibleChange?: (visible: boolean) => void;
    onSubMenuVisibleChange?: (visible: boolean, id: string) => void;
    itemClickCallBack?: (id: string, data?: any) => void;
    selectable?: boolean;
}

interface PopupMenuProps extends Config {
    selectedMap?: Map<string, boolean>;
    menuData: any[] | null;
}

interface PopupMenuState {
    isShown: boolean;
    config: Config;
    elementMap: Map<string, React.ReactNode>;
    disableMap: Map<string, boolean>;
    selectedMap: Map<string, boolean>;
    hiddenMap: Map<string, boolean>;
    customTitleMap: Map<string, string>;
    menuList: any[];
    clickableElementMap: Map<string, React.ReactNode>;
}
/**
 * 菜单公用组件
 */
export default class PopupMenu extends Component<PopupMenuProps, PopupMenuState> {
    public static menuIndex = 0;
    /**
     * 获取菜单项后面的icon或者快捷键
     * @param props
     * @returns {undefined|*|JSX.Element}
     */
    public static getDescription(props?: { shortcut?: string; appendIcon?: string }) {
        const { shortcut, appendIcon } = props || {};
        if (shortcut) {
            return getPlatformShortcut(shortcut);
        } if (appendIcon) {
            return <img src={appendIcon} style={{
                position: 'relative',
                top: '2px',
            }} />;
        }
        return undefined;
    }
    /**
     * 创建一个菜单，通过闭包的方式保存菜单实例
     *
     * @param menuData
     * @param config  配置项，包括弹出位置，菜单的宽度等等,如 fix：true固定位置，scrollable：true，超过800可滚动
     * @param onMenuItemClickCallBack  菜单点击回调，返回menuData的ID进行事件区分
     * @returns {{show: show, hide: hide, remove: remove}}
     */
    public static createMenu(
        menuData: any[]| null, config: Config,
        onMenuItemClickCallBack: (id: string, data?: any) => void,
    ) {
        const div = document.createElement('div');
        // @ts-ignore
        div.fakeID = 'popup-menu-container-id';
        // @ts-ignore
        // eslint-disable-next-line no-plusplus
        const menuID = `${div.fakeID}-${PopupMenu.menuIndex++}`;
        div.id = menuID;
        document.body.appendChild(div);

        let currentConfig = {
            ...config,
            itemClickCallBack: onMenuItemClickCallBack,
        };
        // eslint-disable-next-line react/no-render-return-value
        let MenuInstance: PopupMenu| null = ReactDOM.render(
            <PopupMenu {...currentConfig} menuData={menuData}/>,
            div,
        ) as unknown as PopupMenu;

        function show(newConfig: Config, onMenuItemClickCallBack) {
            if (onMenuItemClickCallBack) {
                currentConfig = {
                    ...currentConfig,
                    ...newConfig,
                    itemClickCallBack: onMenuItemClickCallBack,
                };
            } else {
                currentConfig = {
                    ...currentConfig,
                    ...newConfig,
                };
            }

            if (MenuInstance) {
                MenuInstance.showMenu(currentConfig);
            }
        }

        function hide() {
            if (MenuInstance) {
                MenuInstance.hideMenu();
            }
        }

        function remove() {
            MenuInstance = null;
            ReactDOM.unmountComponentAtNode(div);
        }

        function isShown() {
            if (MenuInstance) {
                return MenuInstance.isShown();
            }
            return false;
        }

        function getMenuID() {
            return menuID;
        }

        // eslint-disable-next-line consistent-return
        function setCustomElement(itemID, element) {
            if (MenuInstance) {
                return MenuInstance.setCustomElement(itemID, element);
            }
        }

        function setClickableCustomElement(itemID, element) {
            if (MenuInstance) {
                return MenuInstance.setClickableCustomElement(itemID, element);
            }
        }

        function setDisableWithIds(idArray) {
            if (MenuInstance) {
                MenuInstance.setDisableWithIds(idArray);
            }
        }

        function setEnableWithIds(idArray) {
            if (MenuInstance) {
                MenuInstance.setEnableWithIds(idArray);
            }
        }

        function clearAllDisableState() {
            if (MenuInstance) {
                MenuInstance.clearAllDisableState();
            }
        }

        function setSelectedWithId(id, unSelectOthers) {
            if (MenuInstance) {
                MenuInstance.setSelectedWithId(id, unSelectOthers);
            }
        }

        function clearChildrenSelected(parenId) {
            if (MenuInstance) {
                MenuInstance.clearChildrenSelected(parenId);
            }
        }

        function setUnSelectedWithId(id) {
            if (MenuInstance) {
                MenuInstance.setUnSelectedWithId(id);
            }
        }

        function isSelected(id) {
            if (MenuInstance) {
                return MenuInstance.isSelected(id);
            }
            return false;
        }

        function updateMenuData(menuData_) {
            if (MenuInstance) {
                MenuInstance.updateMenuData(menuData_);
            }
        }

        function setVisibleWithId(id) {
            if (MenuInstance) {
                MenuInstance.setVisibleWithId(id);
            }
        }

        function setInVisibleWithId(id) {
            if (MenuInstance) {
                MenuInstance.setInVisibleWithId(id);
            }
        }

        function setTitle(id, title) {
            if (MenuInstance) {
                MenuInstance.setTitle(id, title);
            }
        }

        // eslint-disable-next-line consistent-return
        function getDefaultTitle(id) {
            if (MenuInstance) {
                return MenuInstance.getDefaultTitle(id);
            }
        }

        function setDataWithId(id, data) {
            if (MenuInstance) {
                MenuInstance.setDataWithId(id, data);
            }
        }

        function hasMenuId(id) {
            if (MenuInstance) {
                return MenuInstance.hasMenuId(id);
            }
            return false;
        }

        // eslint-disable-next-line consistent-return
        function getChildren(id) {
            if (MenuInstance) {
                return MenuInstance.getChildren(id);
            }
        }

        return {
            // eslint-disable-next-line max-len
            show, hide, remove, isShown, getMenuID, setCustomElement, setClickableCustomElement, setDisableWithIds, setEnableWithIds, clearAllDisableState, setSelectedWithId, clearChildrenSelected, setUnSelectedWithId, isSelected, updateMenuData, setVisibleWithId, setInVisibleWithId, setDataWithId, setTitle, getDefaultTitle, hasMenuId, getChildren,
        };
    }

    private invalidIdMap: Map<string, boolean>;
    private dataMap: Map<string, any>;
    private firstRender: boolean;
    private menuDom: any;
    public constructor(props: PopupMenuProps, context: any) {
        super(props, context);

        const elementMap = new Map();  // 用来存在自定义的菜单项
        const disableMap = new Map();  // 用来存菜单项disable状态
        const selectedMap = new Map();  // 选中的item项
        const clickableElementMap = new Map(); // 可点击的自定义项
        if (props.selectedMap) {
            for (const [key] of props.selectedMap) {
                selectedMap.set(key, true);
            }
        }
        const hiddenMap = new Map();    // 隐藏item项集合
        const customTitleMap = new Map();  // 自定义title
        this.invalidIdMap = new Map();  // 无效ID集合
        this.dataMap = new Map();  // 传给click的data

        this.state = {
            isShown: false,
            config: {},
            elementMap,
            disableMap,
            selectedMap,
            hiddenMap,
            customTitleMap,
            menuList: [],
            clickableElementMap,
        };

        this.firstRender = true;
    }

    public componentDidMount() {
        const config = { ...this.props };
        this.setState({
            config,
        });

        this.updateMenuData(this.props.menuData);
    }

    public componentWillUnmount() {
        document.body.removeEventListener('click', this.bodyClickHandler);
        window.removeEventListener('resize', this.resize);
    }

    public render() {
        if (this.state.menuList.length === 0) return null;

        this.invalidIdMap.clear();
        const {
            top, left, right, minWidth, hasAnim, zIndex,
        } = this.state.config;

        const menuStyle: React.CSSProperties = {
            left, top, right, minWidth, zIndex,
            userSelect: 'none', WebkitUserSelect: 'none',
        };
        if (!hasAnim) {
            menuStyle.display = this.state.isShown ? 'block' : 'none';
        }

        const containerClass = classNames({
            [Style['popup-menu-container']]: true,
            [Style['popup-menu-anim']]: hasAnim,
            [Style['popup-menu-anim-hidden']]: hasAnim && (!this.state.isShown || this.firstRender),
            [Style['popup-menu-anim-visible']]: hasAnim && this.state.isShown && !this.firstRender,
        });
        const menuClass = classNames({
            'menu-scroll': this.state.config.scrollable,
            'popup-menu-for-e2e-locate': true,
        });

        this.firstRender = false;

        const selectedIds: string[] = [];
        for (const [key] of this.state.selectedMap) {
            selectedIds.push(key);
        }

        return (
            <div ref={(ref) => {
                this.menuDom = ref;
            }}
            style={menuStyle}
            className={containerClass}
            id={this.state.config.id}>
                <Menu
                    onClick={this.menuItemClick}
                    visible={this.state.isShown}
                    selectedIds={selectedIds}
                    className={menuClass}
                    // 未发现有可选项
                    // 现在有了
                    selectable={this.state.config?.selectable || false}
                >
                    {this.renderMenuItem(this.state.menuList)}
                </Menu>
            </div>
        );
    }
    private bodyClickHandler = (e: MouseEvent) => {
        // 延迟一下，让body里的元素先执行click
        setTimeout(() => {
            if (this.state.isShown) {
                const clickOnMenu = this.clickOnMenu(e, 15);
                const shouldHide = this.state.config?.bodyClickCallBack
                    ? this.state.config.bodyClickCallBack(e, clickOnMenu) : !clickOnMenu;
                if (shouldHide) {
                    this.hideMenu();
                }
            }
        }, 50);
    };

    // 点击的位置是否在菜单组件上
    private clickOnMenu = (e: MouseEvent, maxLevel: number) => {
        let curDom = e.target as HTMLElement;
        let level = 0;
        while (curDom && level < maxLevel) {  // 只查到15层，避免过多查找
            curDom = curDom.parentNode as HTMLElement;
            level = level + 1;
            if (curDom?.id && curDom.id.indexOf('popup-menu-container-id') >= 0) {
                return true;
            }
        }
        return false;
    };
    // 位置自动调整
    private adjustMenuLoc = (newConfig: Config) => {
        const maxHeight = 800;
        const itemDom = this.menuDom;
        if (itemDom && newConfig) {
            const rect = itemDom.childNodes[0].getBoundingClientRect();
            const { clientHeight } = document.body;

            if (newConfig.fix) {
                // 位置固定
                this.calcFixedMenuProperties(newConfig, {
                    bodyClientHeight: clientHeight,
                    maxHeight,
                    menuHeight: rect.height,
                });
            } else {
                // 位置不固定
                this.calcUnfixedMenuProperties(newConfig, {
                    bodyClientHeight: clientHeight,
                    maxHeight,
                    rect,
                });
            }

            this.setState({
                config: {
                    ...newConfig,
                },
            });
        }
    };

    private calcFixedMenuProperties(newConfig: Config, {
        bodyClientHeight,
        maxHeight,
        menuHeight,
    }: { bodyClientHeight: number; maxHeight: number; menuHeight: number }) {
        // 修改外部的传入的 newConfig;
        const config = newConfig;
        const { top = 0 } = newConfig;
        if (top + menuHeight >= bodyClientHeight || menuHeight >= maxHeight) {
            config.scrollable = true;
            if (menuHeight >= maxHeight && maxHeight < bodyClientHeight - top) {
                config.height = maxHeight;
            } else {
                config.height = bodyClientHeight - top - 6;
            }
        }
    }

    private calcUnfixedMenuProperties(newConfig: Config, {
        bodyClientHeight,
        maxHeight,
        rect,
    }: { bodyClientHeight: number; maxHeight: number; rect: DOMRect }) {
        const config = newConfig;
        const { top = 0 } = newConfig;
        const { height: menuHeight } = rect;

        if (menuHeight >= bodyClientHeight) {  // 如果menu大于整体高度，可滚动
            config.scrollable = true;
            config.top = 0;
            config.height = bodyClientHeight;
        } else if (menuHeight >= maxHeight) {
            config.scrollable = true;
            config.height = maxHeight;
        } else if (top + menuHeight >= bodyClientHeight) {  // 下方菜单显示位置不够
            config.scrollable = false;
            config.height = 'auto';
            if (top >= menuHeight) {  // 上方能显示，则在上方显示
                config.top = top - menuHeight;
            } else { // 否则显示在下方，调整top位置
                const offset = top + menuHeight - bodyClientHeight;
                config.top = top - offset;
            }
        } else {
            config.scrollable = false;
            config.height = 'auto';
        }

        // 调整左右位置
        const vw = document.body.clientWidth;
        if (rect.right > vw) {  // 只有左对齐才会出现这种情况
            config.left = (config.left || 0) - (rect.right - vw + 4);
        } else if (rect.left < 0) { // 右对齐出现这种情况
            config.right = vw - rect.width - 4;
        }
    }

    private resize = () => {
        this.hideMenu();
    };

    private showMenu = (newConfig: Config) => {
        /* eslint-disable no-param-reassign */
        if (!this.state.isShown) {
            document.addEventListener('click', this.bodyClickHandler);
            window.addEventListener('resize', this.resize);
            newConfig.height = 'auto';  // 让高度在每次打开菜单时自动计算。高度不可设置，会自动调整
            this.setState({
                isShown: true,
                config: newConfig,
            }, () => {
                this.onVisibleChange(true);
                this.adjustMenuLoc(newConfig);
            });
        } else {
            this.setState({
                config: newConfig,
            }, () => {
                this.adjustMenuLoc(newConfig);
            });
        }
        /* eslint-enable no-param-reassign */
    };

    private hideMenu = () => {
        if (this.state.isShown) {
            this.setState({
                isShown: false,
            }, () => {
                this.onVisibleChange(false);
            });
            document.removeEventListener('click', this.bodyClickHandler);
            window.removeEventListener('resize', this.resize);
            if (this.state.config.onMenuHideCallBack) {
                this.state.config.onMenuHideCallBack();
            }
        }
    };

    private isShown = () => this.state.isShown;

    private hasMenuId = (id: string) => this.hasIdInMenu(id, this.state.menuList);

    private hasIdInMenu = (id: string, list: any[]) => {
        for (let i = 0; i < list.length; i++) {
            const item = list[i];
            if (item.id === id) {
                return true;
            } if (item.children) {
                if (this.hasIdInMenu(id, item.children)) {
                    return true;
                }
            }
        }
        return false;
    };

    private getChildren = (id: string) => this.getChildrenWithIdList(id, this.state.menuList);

    private getChildrenWithIdList = (id: string, list: any[]) => {
        for (let i = 0; i < list.length; i++) {
            const item = list[i];
            if (item.children && item.id === id) {
                return item.children;
            } if (item.children) {
                const child = this.getChildrenWithIdList(id, item.children);
                if (child) return child;
            }
        }
    };

    private getItem = (id: string) => this.getItemWithList(id, this.state.menuList);

    private getItemWithList = (id: string, list: any[]) => {
        for (let i = 0; i < list.length; i++) {
            const item = list[i];
            if (item.id === id) {
                return item;
            } if (item.children) {
                const child = this.getItemWithList(id, item.children);
                if (child) return child;
            }
        }
    };

    private onVisibleChange = (visible: boolean) => {
        if (this.state.config.onVisibleChange) {
            this.state.config.onVisibleChange(visible);
        }
    };

    private onSubMenuVisibleChange = (visible: boolean, id: string) => {
        if (this.state.config.onSubMenuVisibleChange) {
            this.state.config.onSubMenuVisibleChange(visible, id);
        }
    };

    private menuItemClick = (id: string) => {
        if (this.invalidIdMap.get(id)) return;
        if (this.state.config.itemClickCallBack) {
            this.state.config.itemClickCallBack(id, this.dataMap.get(id));
        }
    };

    /**
     * 指定某个菜单项,自定义UI 。 不支持subMenu自定义UI
     * @param itemid
     * @param element 传入的组件,必须是一个React元素
     */
    private setCustomElement = (itemid: string, element: React.ReactNode) => {
        this.state.elementMap.set(itemid, element);
        this.setState({
            elementMap: this.state.elementMap,
        });
    };

    private setClickableCustomElement = (itemid: string, element: React.ReactNode) => {
        this.state.clickableElementMap.set(itemid, element);
        this.setState({
            clickableElementMap: this.state.clickableElementMap,
        });
    };

    private setDisableWithIds = (idArray: string[]) => {
        if (idArray instanceof Array) {
            idArray.forEach((id) => {
                this.state.disableMap.set(id, true);
            });
            this.setState({
                disableMap: this.state.disableMap,
            });
        }
    };

    private setEnableWithIds = (idArray: string[]) => {
        if (idArray instanceof Array) {
            idArray.forEach((id) => {
                this.state.disableMap.delete(id);
            });
            this.setState({
                disableMap: this.state.disableMap,
            });
        }
    };

    private clearAllDisableState = () => {
        this.state.disableMap.clear();
        this.state.hiddenMap.clear();
        this.setState({
            disableMap: this.state.disableMap,
            hiddenMap: this.state.hiddenMap,
        });
    };

    private setSelectedWithId = (itemId: string, unSelectOthers: boolean) => {
        if (itemId) {
            this.state.selectedMap.set(itemId, true);
            if (unSelectOthers) {
                const parentChildren = this.getParentChildrenList(itemId);
                if (parentChildren) {
                    parentChildren.forEach((item) => {
                        if (item.id !== itemId) {
                            this.state.selectedMap.delete(item.id);
                        }
                    });
                }
            }
            this.setState({
                selectedMap: this.state.selectedMap,
            });
        }
    };

    private clearChildrenSelected = (parentId: string) => {
        const children = this.getChildren(parentId);
        if (children && children instanceof Array) {
            children.forEach((item) => {
                this.setUnSelectedWithId(item.id);
            });
        }
    };

    private getParentChildrenList = (itemId: string) => this.getParentChildren(itemId, this.state.menuList);

    private getParentChildren = (id: string, list: any[]) => {
        for (let i = 0; i < list.length; i++) {
            const item = list[i];
            if (item.id === id) {
                return list;
            } if (item.children) {
                const array = this.getParentChildren(id, item.children);
                if (array) return array;
            }
        }
    };

    private setUnSelectedWithId = (itemId: string) => {
        if (itemId) {
            this.state.selectedMap.delete(itemId);
            this.setState({
                selectedMap: this.state.selectedMap,
            });
        }
    };

    private isSelected = (itemId: string) => {
        if (itemId) {
            const selected = this.state.selectedMap.get(itemId) || false;
            return selected;
        }
        return false;
    };

    private setDataWithId = (itemId: string, data: any) => {
        if (itemId) {
            this.dataMap.set(itemId, data);
        }
    };

    private updateMenuData = (menuData: any[] | null) => {
        if (menuData instanceof Array) {
            // @ts-ignore
            // eslint-disable-next-line react/no-direct-mutation-state
            this.state.menuList = menuData;
            this.setDataWithList(menuData);
            this.setState({
                menuList: menuData,
            });
        }
    };

    private setDataWithList = (menuList: any[]) => {
        for (let i = 0; i < menuList.length; i++) {
            const item = menuList[i];
            if (item.data) {
                this.setDataWithId(item.id, item.data);
            }
            if (item.children) {
                this.setDataWithList(item.children);
            }
        }
    };

    // 显示某个item
    private setVisibleWithId = (itemId: string) => {
        if (itemId) {
            this.state.hiddenMap.delete(itemId);
            this.setState({
                hiddenMap: this.state.hiddenMap,
            });
        }
    };

    // 隐藏某个item
    private setInVisibleWithId = (itemId: string) => {
        if (itemId) {
            this.state.hiddenMap.set(itemId, true);
            this.setState({
                hiddenMap: this.state.hiddenMap,
            });
        }
    };

    private setTitle = (itemId: string, title: string) => {
        if (itemId) {
            this.state.customTitleMap.set(itemId, title);
            this.setState({
                customTitleMap: this.state.customTitleMap,
            });
        }
    };

    private getDefaultTitle = (itemId: string) => {
        const item = this.getItem(itemId);
        if (item) {
            return item.title;
        }
    };

    private hasTwoDivider(lastItem: any, item: any) {
        return lastItem && lastItem.id === 'divider-menu-item' && item.id === 'divider-menu-item';
    }

    private getLastItem(prev: any[]) {
        return prev.length > 0 ? prev[prev.length - 1] : null;
    }

    private renderMenuItem = (data: any[]) => {
        const menuItemList = data.reduce((prev, item) => {
            // 如果上一个是分割线，当前也是分割线，则不加入
            const lastItem = this.getLastItem(prev);
            if (this.hasTwoDivider(lastItem, item)) {
                return prev;
            }
            // 不隐藏则加入
            const itemHidden = this.state.hiddenMap.get(item.id) || false;
            if (!itemHidden) {
                prev.push(item);
            }
            // 插件项手动修改大小
            if (item.id === ActionType.ADDON && item.children?.length) {
                item.children.forEach((addonItem) => {
                    addonItem.style = {
                        paddingLeft: '16px',
                    };
                });
            }
            return prev;
        }, []);
        const itemList = menuItemList.map((item, index) => {
            const key = item.id;
            const disable = this.state.disableMap.get(item.id) || false;
            const itemStyle = { minWidth: this.state.config.subMinWidth! - 40, ...item.style };
            if (key === 'divider-menu-item') {
                return <Divider key={index} />;
            } if (item.children) {
                this.invalidIdMap.set(item.id, true);
                const customTitle = this.state.customTitleMap.get(item.id);
                return (
                    this.renderSubMenu(customTitle, item, disable)
                );
            }
            const customElement = this.state.elementMap.get(item.id);
            if (customElement) {
                this.invalidIdMap.set(item.id, true);
                const itemClass = classNames({
                    [Style['popup-menu-item-custom']]: true,
                    [Style.disable]: true,
                });
                return (
                    <Menu.Item id={key} key={key} className={itemClass}>
                        {customElement}
                    </Menu.Item>
                );
            }

            const clickableCustomElement = this.state.clickableElementMap.get(item.id);
            if (clickableCustomElement) {
                const itemClass = classNames({
                    [Style['popup-menu-item-custom']]: true,
                });
                return (
                    <Menu.Item id={key} key={key} className={itemClass}>
                        {clickableCustomElement}
                    </Menu.Item>
                );
            }

            const customTitle = this.state.customTitleMap.get(item.id);
            return (
                this.rederItem(customTitle, item, disable, itemStyle)
            );
        });
        return itemList;
    };

    private isDisabledSelectableMenu = (id: string) => {
        const selectedDisabledMap = {
            [ActionType.DELETE_ROW_COL]: true,
            ['right-menu-id-Shape']: true,
        };

        return !selectedDisabledMap[id];
    };

    private renderSubMenu(customTitle: string | undefined, item: any, disable: boolean) {
        const key = item.id;
        let content = customTitle || item.title;
        const { badge } = item;
        const constructor = (content: React.ReactNode) => (
            <Menu.SubMenu
                id={key}
                ref={(ref) => {
                    this[item.id] = ref;
                }}
                popupStyle={ key === ActionType.QUICK_TOOL ? { paddingBottom: 0 } : undefined }
                key={`${key}-sub`}
                disabled={disable}
                title={content}
                onVisibleChange={visible => this.onSubMenuVisibleChange(visible, key)}
                // props.id 属性没有相关描述，当 id 为 right-menu-id-Shape 时，禁用 SubMenu 的 selectable 属性
                selectable={this.isDisabledSelectableMenu(key)}
            >
                {disable ? null : this.renderMenuItem(item.children)}
            </Menu.SubMenu>
        );
        if (badge?.visible) {
            content = (
                <li className={Style['menu-item-content']}>
                    {content}
                    {/* <Badge count={'NEW'} className={Style['menu-item-badge']}/> */}
                    <div className={Style['menu-item-badge']}>
                        <span>NEW</span>
                    </div>
                </li>
            );
        }
        return constructor(content);
    }

    private rederItem(customTitle: string | undefined, item: any, disable: boolean, itemStyle: React.CSSProperties) {
        const key = item.id;
        let content = customTitle || item.title;
        const { badge, addon, extraAddonId } = item;
        let constructor;
        if (addon) {
            const { icon, title, vendorSite, imageUrl, description, vendorName } = {
                icon: addon.icons.small,
                title: addon.name,
                vendorName: addon.vendorName,
                vendorSite: addon.vendorSite,
                imageUrl: addon.icons.intro,
                description: addon.shortDesc,
            };
            if (extraAddonId === addon.addonId) {
                content = <li className={Style['menu-item-content']}>
                    {content}
                    <div className={Style['tag-same-wrapper']}>
                        <div className={Style['tag-same-text']}>{i18n.t('同款')}</div>
                    </div>
                </li>;
            }
            constructor = content => (
                <Tooltip
                    style={{ display: 'block' }}
                    placement={['right', 'left']}
                    title={
                        <IntelligentToolDescriptionCard
                            vendorName={vendorName}
                            vendorSite={vendorSite}
                            title={title}
                            imageUrl={imageUrl}
                            description={description} />}
                >
                    <Menu.Item id={key} key={key}
                        style={{
                            paddingLeft: '16px',
                        }}
                        icon={icon}
                        disabled={disable}
                        description={getPlatformShortcut(item.shortcut)}>
                        {content}
                    </Menu.Item>
                </Tooltip>);
        } else {
            constructor = (content: React.ReactNode) => {
                const isVipLink = key === ActionType.VIP_LINK_TEXT;
                if (isVipLink) {
                    content = getVipLinkText();
                }
                return <Menu.Item id={key} key={key} data-menu-item-id={item.id}
                    disabled={disable}
                    style={itemStyle}
                    description={PopupMenu.getDescription({
                        shortcut: item.shortcut,
                        appendIcon: item.appendIcon,
                    })}>
                    {content}
                </Menu.Item>;
            };
        }
        if (badge?.visible) {
            content = <li className={Style['menu-item-content']}>
                {content}
                {/* <Badge count={'NEW'} className={Style['menu-item-badge']}/> */}
                <div className={Style['menu-item-badge']}><span>NEW</span></div>
            </li>;
        }
        if (disable && item.disabledContent) {
            return (<Tooltip title={item.disabledContent} placement="left" style={{ display: 'block' }}>{constructor(content)}</Tooltip>);
        }
        return constructor(content);
    }
}
